import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid } from 'v3/Components/DataGrid/DataGrid';
import { BooleanSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/BooleanSelectFilter';
import { DateRangePickerFilterFilter } from 'v3/Components/DataGrid/ColumnFilters/DateRangePickerFilter';
import { MultiSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/MultiSelectFilter';
import { SingleSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/SingleSelectFilter';
import { TextFieldFilter } from 'v3/Components/DataGrid/ColumnFilters/TextFieldFilter';
import { StatusPill } from 'v3/Components/Shared/StatusPill';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import {
  buildEndpoint,
  buildRoute,
  defaultDataTransform,
  useDataFetcher,
  formatShortDate,
  formatShortDateTime,
} from 'Utilities';
import { useEnums, useGlobalContext } from 'Context';
import { useHistory } from 'react-router-dom';
import { AddEditSubRoute, ROUTES } from 'Navigation';
import ClientSelector from 'v3/Components/Shared/ClientSelector';
import useClientSearchParams from 'v3/Utilities/useClientSearchParams';
import NewReferralButton from 'v3/Components/Shared/NewReferralButton';
import { LineOfBusinessEnum } from 'v3/Models/Enums';
import './UnderReviewQueue.scss';

interface DropdownOptions {
  clients: { label: string; value: string }[];
  reviewers: { label: string; value: string }[];
  owners: { label: string; value: string }[];
  states: { label: string; value: string }[];
  specialties: { label: string; value: string }[];
  apContactPreferences: { label: string; value: string }[];
  tatPriorities: { label: string; value: string }[];
  tats: { label: string; value: string }[];
  genders: { label: string; value: string }[];
  lobs: { label: string; value: string }[];
  services: { label: string; value: string }[];
  subServices: { label: string; value: string }[];
  proxiedByUsers: string[];
}

const renderSpecialtyCell = (params: any) => {
  const specialties: { displayName: string }[] = params.row.specialty ?? [];
  if (specialties.length === 0) return null;
  return (
    <span className="specialty-cell">
      <span>{specialties[0]?.displayName}</span>
      {specialties.length > 1 && (
        <span className="specialty-cell__extra">+{specialties.length - 1}</span>
      )}
    </span>
  );
};

const buildPeerReviewColumns = (opts: DropdownOptions): any[] => [
  {
    field: 'stradixNumber',
    headerName: 'Stradix #',
    width: 188,
    hideable: false,
    pinnable: false,
    cellClassName: 'under-review-queue-cell-bold',
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimant',
    headerName: 'Claimant Name',
    width: 200,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'client',
    headerName: 'Client',
    width: 200,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.clients} />
    ),
  },
  {
    field: 'claimNumber',
    headerName: 'Claim/Client #',
    width: 160,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'reportStatus',
    headerName: 'Report Status',
    width: 160,
    hideable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'reportDueDate',
    headerName: 'Report Due Date',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'dueDate',
    headerName: 'Client Due Date',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'reviewer',
    headerName: 'Reviewer',
    width: 180,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.reviewers} />
    ),
  },
  {
    field: 'benefitState',
    headerName: 'State',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.states} />
    ),
  },
  {
    field: 'specialty',
    headerName: 'Specialty',
    width: 200,
    hideable: true,
    sortable: false,
    type: 'singleSelect',
    renderCell: renderSpecialtyCell,
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.specialties} />
    ),
  },
  {
    field: 'subspecialty',
    headerName: 'Subspecialty',
    width: 240,
    hideable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'apContactPreference',
    headerName: 'AP Contact Pref.',
    width: 160,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter {...params} optionItems={opts.apContactPreferences} />
    ),
  },
  {
    field: 'numberOfAttendingProviders',
    headerName: '# of AP',
    width: 120,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter
        {...params}
        optionItems={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
          label: String(n),
          value: String(n),
        }))}
      />
    ),
  },
  {
    field: 'subService',
    headerName: 'Sub Service',
    width: 220,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.subServices} />
    ),
  },
  {
    field: 'service',
    headerName: 'Service',
    width: 200,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.services} />
    ),
  },
  {
    field: 'tatPriority',
    headerName: 'TAT Priority',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderCell: (params: any) =>
      params.value ? <StatusPill status={params.value} /> : null,
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter {...params} optionItems={opts.tatPriorities} />
    ),
  },
  {
    field: 'referralDate',
    headerName: 'Referral Date',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'referralSource',
    headerName: 'Referrer',
    width: 180,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'lob',
    headerName: 'LOB',
    width: 260,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.lobs} />
    ),
  },
  {
    field: 'dateOfInjury',
    headerName: 'DOI/DOD',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDate(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} dateOnly />
    ),
  },
  {
    field: 'proxiedBy',
    headerName: 'Proxied By',
    width: 180,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.proxiedByUsers} />
    ),
  },
  {
    field: 'tat',
    headerName: 'TAT',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter {...params} optionItems={opts.tats} />
    ),
  },
  {
    field: 'callAttempts',
    headerName: 'Call Attempts',
    width: 140,
    hideable: true,
    type: 'number',
  },
  {
    field: 'referralNumber',
    headerName: 'Referral #',
    width: 160,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'employerName',
    headerName: 'Employer Name',
    width: 200,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'pageCount',
    headerName: 'Page Count',
    width: 140,
    hideable: true,
    type: 'number',
  },
  {
    field: 'jmRequired',
    headerName: 'JM Required',
    width: 140,
    hideable: true,
    renderCell: (params: any) => {
      if (params.value === true) return <StatusPill status="Yes" />;
      if (params.value === false) return <StatusPill status="No" />;
      return null;
    },
    renderHeaderFilter: (params: any) => <BooleanSelectFilter {...params} />,
  },
  {
    field: 'attorneyName',
    headerName: 'Attorney Name',
    width: 180,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'dateOfRequest',
    headerName: 'Date of Request',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'dateOfExam',
    headerName: 'Date of Exam',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'appointmentDate',
    headerName: 'Appointment Date',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
  },
  {
    field: 'requestReceivedDate',
    headerName: "Date & Time Request Rec'd",
    width: 220,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'team',
    headerName: 'Team',
    width: 140,
    hideable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'externalAdjuster',
    headerName: 'External Adjuster',
    width: 180,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'gender',
    headerName: 'Gender',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter {...params} optionItems={opts.genders} />
    ),
  },
  {
    field: 'claimantEmail',
    headerName: 'Claimant Email',
    width: 200,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'dateOfBirth',
    headerName: 'DOB',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDate(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} dateOnly />
    ),
  },
  {
    field: 'owner',
    headerName: 'Owner',
    width: 180,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'qaOwnerName',
    headerName: 'QA Owner',
    width: 180,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'recentUser',
    headerName: 'Recent User',
    width: 180,
    hideable: true,
    sortable: false,
    filterable: false,
  },
];

const buildImeColumns = (opts: DropdownOptions): any[] => [
  {
    field: 'stradixNumber',
    headerName: 'Stradix #',
    width: 188,
    hideable: false,
    pinnable: false,
    cellClassName: 'under-review-queue-cell-bold',
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimant',
    headerName: 'Claimant Name',
    width: 200,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'client',
    headerName: 'Client',
    width: 200,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.clients} />
    ),
  },
  {
    field: 'claimNumber',
    headerName: 'Claim/Client #',
    width: 160,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'referralDate',
    headerName: 'Referral Date',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'scheduledOn',
    headerName: 'Scheduled On',
    width: 160,
    hideable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'lettersSent',
    headerName: 'Letters Sent',
    width: 140,
    hideable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'reminderCall',
    headerName: 'Reminder Call',
    width: 160,
    hideable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'appointmentDate',
    headerName: 'Appointment Date',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'attendance',
    headerName: 'Attendance',
    width: 140,
    hideable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'reportStatus',
    headerName: 'Report Status',
    width: 160,
    hideable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'reportDueDate',
    headerName: 'Report Due Date',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'dueDate',
    headerName: 'Client Due Date',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'reviewer',
    headerName: 'Reviewer',
    width: 180,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.reviewers} />
    ),
  },
  {
    field: 'benefitState',
    headerName: 'State',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.states} />
    ),
  },
  {
    field: 'specialty',
    headerName: 'Specialty',
    width: 200,
    hideable: true,
    sortable: false,
    type: 'singleSelect',
    renderCell: renderSpecialtyCell,
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.specialties} />
    ),
  },
  {
    field: 'subspecialty',
    headerName: 'Subspecialty',
    width: 240,
    hideable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'subService',
    headerName: 'Sub Service',
    width: 220,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.subServices} />
    ),
  },
  {
    field: 'service',
    headerName: 'Service',
    width: 200,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.services} />
    ),
  },
  {
    field: 'tatPriority',
    headerName: 'TAT Priority',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderCell: (params: any) =>
      params.value ? <StatusPill status={params.value} /> : null,
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter {...params} optionItems={opts.tatPriorities} />
    ),
  },
  {
    field: 'referralSource',
    headerName: 'Referrer',
    width: 180,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimantStreetAddress',
    headerName: 'Claimant Street Address',
    width: 400,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimantCity',
    headerName: 'Claimant City',
    width: 260,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimantState',
    headerName: 'Claimant State',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.states} />
    ),
  },
  {
    field: 'claimantZipCode',
    headerName: 'Claimant Zip Code',
    width: 140,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimantEmail',
    headerName: 'Claimant Email',
    width: 200,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimantPhone',
    headerName: 'Claimant Phone #',
    width: 160,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'dateOfExam',
    headerName: 'Date of Exam',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'owner',
    headerName: 'Owner',
    width: 180,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'qaOwnerName',
    headerName: 'QA Owner',
    width: 180,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'recentUser',
    headerName: 'Recent User',
    width: 180,
    hideable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'numberOfAttendingProviders',
    headerName: '# of AP',
    width: 120,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter
        {...params}
        optionItems={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
          label: String(n),
          value: String(n),
        }))}
      />
    ),
  },
  {
    field: 'apContactPreference',
    headerName: 'AP Contact Pref.',
    width: 160,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter {...params} optionItems={opts.apContactPreferences} />
    ),
  },
  {
    field: 'lob',
    headerName: 'LOB',
    width: 260,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.lobs} />
    ),
  },
  {
    field: 'proxiedBy',
    headerName: 'Proxied By',
    width: 180,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <MultiSelectFilter {...params} optionItems={opts.proxiedByUsers} />
    ),
  },
  {
    field: 'tat',
    headerName: 'TAT',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter {...params} optionItems={opts.tats} />
    ),
  },
  {
    field: 'callAttempts',
    headerName: 'Call Attempts',
    width: 140,
    hideable: true,
    type: 'number',
  },
  {
    field: 'referralNumber',
    headerName: 'Referral #',
    width: 160,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'employerName',
    headerName: 'Employer Name',
    width: 200,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'pageCount',
    headerName: 'Page Count',
    width: 140,
    hideable: true,
    type: 'number',
  },
  {
    field: 'jmRequired',
    headerName: 'JM Required',
    width: 140,
    hideable: true,
    renderCell: (params: any) => {
      if (params.value === true) return <StatusPill status="Yes" />;
      if (params.value === false) return <StatusPill status="No" />;
      return null;
    },
    renderHeaderFilter: (params: any) => <BooleanSelectFilter {...params} />,
  },
  {
    field: 'attorneyName',
    headerName: 'Attorney Name',
    width: 180,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'dateOfRequest',
    headerName: 'Date of Request',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'requestReceivedDate',
    headerName: "Date & Time Request Rec'd",
    width: 220,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDateTime(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} />
    ),
  },
  {
    field: 'team',
    headerName: 'Team',
    width: 140,
    hideable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'externalAdjuster',
    headerName: 'External Adjuster',
    width: 180,
    hideable: true,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'gender',
    headerName: 'Gender',
    width: 140,
    hideable: true,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => (
      <SingleSelectFilter {...params} optionItems={opts.genders} />
    ),
  },
  {
    field: 'dateOfBirth',
    headerName: 'DOB',
    width: 200,
    widthOnFilter: 260,
    hideable: true,
    valueFormatter: (value: string) => formatShortDate(value),
    renderHeaderFilter: (params: any) => (
      <DateRangePickerFilterFilter {...params} dateOnly />
    ),
  },
];

const imeColumnVisibility = {
  recentUser: false,
  numberOfAttendingProviders: false,
  apContactPreference: false,
  lob: false,
  proxiedBy: false,
  tat: false,
  callAttempts: false,
  referralNumber: false,
  employerName: false,
  pageCount: false,
  jmRequired: false,
  attorneyName: false,
  dateOfRequest: false,
  requestReceivedDate: false,
  team: false,
  externalAdjuster: false,
  gender: false,
  dateOfBirth: false,
};

const peerReviewColumnVisibility = {
  lob: false,
  dateOfInjury: false,
  proxiedBy: false,
  tat: false,
  callAttempts: false,
  referralNumber: false,
  employerName: false,
  pageCount: false,
  jmRequired: false,
  attorneyName: false,
  dateOfRequest: false,
  dateOfExam: false,
  requestReceivedDate: false,
  team: false,
  externalAdjuster: false,
  gender: false,
  claimantEmail: false,
  dateOfBirth: false,
  recentUser: false,
};

const useUnderReviewQueueDropdownOptions = (): DropdownOptions => {
  const enums: any = useEnums();

  const filterOptionsEndpoint =
    ENDPOINTS.underreviewqueue.endpointString('FilterOptions');

  const [clientsList] = useDataFetcher<{ id: string; name: string }[]>(
    buildEndpoint(ENDPOINTS.clients.dropdownItems),
    [],
    defaultDataTransform
  );
  const [filterOptions] = useDataFetcher<{
    owners: { id: string; name: string }[];
    reviewers: { id: string; name: string }[];
    services: { id: string; name: string }[];
    subServices: { id: string; name: string }[];
    proxiedByUsers: string[];
  }>(
    buildEndpoint(filterOptionsEndpoint),
    {
      owners: [],
      reviewers: [],
      services: [],
      subServices: [],
      proxiedByUsers: [],
    },
    defaultDataTransform,
    undefined,
    true,
    '3.0'
  );

  return {
    clients: (clientsList ?? [])
      .map((c) => ({ label: c.name, value: c.id }))
      .filter((c) => c.label && c.value),
    reviewers: (filterOptions?.reviewers ?? []).map((r) => ({
      label: r.name,
      value: r.id,
    })),
    owners: (filterOptions?.owners ?? []).map((o) => ({
      label: o.name,
      value: o.id,
    })),
    states: (enums?.state ? Object.values(enums.state) : []).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    specialties: Object.values(enums?.specialty ?? {}).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    apContactPreferences: Object.values(enums?.apContact ?? {}).map(
      (e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })
    ),
    tatPriorities: Object.values(enums?.tatPriority ?? {}).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    tats: Object.values(enums?.tat ?? {}).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    genders: Object.values(enums?.gender ?? {}).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    lobs: Object.values(enums?.lineOfBusiness ?? {}).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    services: (filterOptions?.services ?? []).map((s) => ({
      label: s.name,
      value: s.id,
    })),
    subServices: (filterOptions?.subServices ?? []).map((s) => ({
      label: s.name,
      value: s.id,
    })),
    proxiedByUsers: filterOptions?.proxiedByUsers ?? [],
  };
};

export const UnderReviewQueuePage: React.FC = () => {
  const history = useHistory();
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const isInternal = userdata?.isInternal ?? false;
  const readEndpoint = buildEndpoint(ENDPOINTS.underreviewqueue.read);
  const exportEndpoint = buildEndpoint(
    ENDPOINTS.underreviewqueue.endpointString('ExportToCsv')
  );
  const dropdownOptions = useUnderReviewQueueDropdownOptions();
  const peerReviewColumns = React.useMemo(
    () => buildPeerReviewColumns(dropdownOptions),
    [dropdownOptions]
  );
  const imeColumns = React.useMemo(
    () => buildImeColumns(dropdownOptions),
    [dropdownOptions]
  );

  const peerReviewLegacyParams = React.useMemo(
    () => ({
      readEndpoint,
      searchParams: {
        excludeLineOfBusiness: { value: LineOfBusinessEnum.IME },
      },
      apiVersion: '3.0',
    }),
    [readEndpoint]
  );

  const imeLegacyParams = React.useMemo(
    () => ({
      readEndpoint,
      searchParams: { lineOfBusiness: { value: LineOfBusinessEnum.IME } },
      apiVersion: '3.0',
    }),
    [readEndpoint]
  );

  const { setSelectedClient, clientSearchParams } = useClientSearchParams();

  const handleRowClick = (params: any) => {
    const referralId = params.row?.referralId;
    if (referralId) {
      history.push(
        buildRoute(AddEditSubRoute.Referral, referralId, ROUTES.VIEW)
      );
    }
  };

  return (
    <div className="underReviewQueue">
      <div className="main-grid-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h1>Under Review Queue</h1>
          <div className="grids-header-client-selector">
            <ClientSelector
              containerClassName="client-selector--large"
              onClientChange={setSelectedClient}
            />
          </div>
        </div>
        <NewReferralButton
          text={userdata?.isInternal ? 'Add Case' : 'Add Referral'}
          startIcon={<AddIcon />}
        />
      </div>
      <div className="reviewers-content">
        <DataGrid
          onRowClick={handleRowClick}
          title="Peer Review Cases"
          gridId="underReviewQueuePeerReview"
          enableUrlSync
          urlPrefix="pr"
          legacyParams={peerReviewLegacyParams}
          columns={peerReviewColumns}
          columnVisibilityModel={peerReviewColumnVisibility}
          pinnedColumns={{ left: ['stradixNumber'] }}
          additionalSearchParams={clientSearchParams}
          exportEndpoint={exportEndpoint}
          exportFileName="UnderReviewQueue_PeerReview"
          defaultShowFilters={isInternal}
          enableAutoRefresh
        />
        <div className="under-review-queue-grid-gap" />
        <DataGrid
          onRowClick={handleRowClick}
          title="IME Cases"
          gridId="underReviewQueueIME"
          enableUrlSync
          urlPrefix="ime"
          legacyParams={imeLegacyParams}
          columns={imeColumns}
          columnVisibilityModel={imeColumnVisibility}
          pinnedColumns={{ left: ['stradixNumber'] }}
          additionalSearchParams={clientSearchParams}
          exportEndpoint={exportEndpoint}
          exportFileName="UnderReviewQueue_IME"
          defaultShowFilters={isInternal}
          enableAutoRefresh
        />
      </div>
    </div>
  );
};
